import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ExternalLink, CheckSquare } from 'lucide-react';
import { Project, Task } from '@/types/product-manager';
import { Link } from 'react-router-dom';
import * as Icons from 'lucide-react';
import { getTasks } from '@/services/productManagerService';

interface TaskMetrics {
  todo: number;
  inProgress: number;
  completed: number;
}

interface LiveProjectCardProps {
  project: Project;
  viewMode: 'grid' | 'list';
  onUpdate: () => void;
}

const LiveProjectCard: React.FC<LiveProjectCardProps> = ({ project, viewMode, onUpdate }) => {
  const [taskMetrics, setTaskMetrics] = useState<TaskMetrics | null>(null);

  // Get the icon component dynamically
  const IconComponent = project.metadata.appIcon ?
    (Icons as any)[project.metadata.appIcon] || Icons.FolderOpen :
    Icons.FolderOpen;

  // Load task metrics for this project
  useEffect(() => {
    const loadTaskMetrics = async () => {
      try {
        const tasks = await getTasks(project.id);
        const metrics: TaskMetrics = {
          todo: tasks.filter(task => task.status === 'todo').length,
          inProgress: tasks.filter(task => task.status === 'in-progress').length,
          completed: tasks.filter(task => task.status === 'completed').length
        };
        setTaskMetrics(metrics);
      } catch (error) {
        console.error('Error loading task metrics:', error);
        setTaskMetrics(null);
      }
    };

    loadTaskMetrics();
  }, [project.id]);

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'critical': return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
      case 'high': return 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200';
      case 'medium': return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
      case 'low': return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
      default: return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
      case 'completed': return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200';
      case 'archived': return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200';
      default: return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200';
    }
  };

  if (viewMode === 'list') {
    return (
      <Card className="hover:shadow-md transition-shadow">
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4 flex-1">
              <Link to={`/product-manager/projects/${project.id}`} className={`p-2 rounded-lg ${project.metadata.appColor || 'bg-violet-500'} hover:opacity-80 transition-opacity cursor-pointer`}>
                <IconComponent className="h-6 w-6 text-white" />
              </Link>
              
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white truncate">
                      {project.name}
                    </h3>
                  </div>
                  <div className="flex items-center space-x-2 mr-4">
                    <Badge className={getPriorityColor(project.metadata.priority)}>
                      {project.metadata.priority}
                    </Badge>
                    <Badge className={getStatusColor(project.status)}>
                      {project.status}
                    </Badge>
                  </div>
                </div>
                
                {/* Task Metrics - Always reserve space */}
                <div className="text-sm text-gray-600 dark:text-gray-400 mt-1 min-h-[20px]">
                  {taskMetrics && (taskMetrics.todo > 0 || taskMetrics.inProgress > 0 || taskMetrics.completed > 0) && (
                    <>To-Do ({taskMetrics.todo}) In Progress ({taskMetrics.inProgress}) Completed ({taskMetrics.completed})</>
                  )}
                </div>
              </div>
            </div>
            
            <div className="flex items-center space-x-4">
              <Button variant="outline" size="sm" asChild>
                <Link to={`/product-manager/projects/${project.id}`}>
                  <CheckSquare className="h-4 w-4 mr-2" />
                  View Tasks
                </Link>
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="hover:shadow-md transition-shadow">
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between">
          <div className="flex items-center space-x-3">
            <Link to={`/product-manager/projects/${project.id}`} className={`p-2 rounded-lg ${project.metadata.appColor || 'bg-violet-500'} hover:opacity-80 transition-opacity cursor-pointer`}>
              <IconComponent className="h-6 w-6 text-white" />
            </Link>
            <div>
              <CardTitle className="text-lg">{project.name}</CardTitle>
              <div className="flex items-center space-x-2 mt-1">
              </div>
            </div>
          </div>
          
          <div className="flex items-center space-x-2">
            <Badge className={getPriorityColor(project.metadata.priority)}>
              {project.metadata.priority}
            </Badge>
            <Badge className={getStatusColor(project.status)}>
              {project.status}
            </Badge>
          </div>
        </div>
      </CardHeader>
      
      <CardContent>
        {/* Task Metrics - Always reserve space */}
        <div className="text-sm text-gray-600 dark:text-gray-400 mb-4 min-h-[20px]">
          {taskMetrics && (taskMetrics.todo > 0 || taskMetrics.inProgress > 0 || taskMetrics.completed > 0) && (
            <>To-Do ({taskMetrics.todo}) In Progress ({taskMetrics.inProgress}) Completed ({taskMetrics.completed})</>
          )}
        </div>
        
        <div className="flex space-x-2">
          <Button variant="outline" size="sm" className="flex-1" asChild>
            <Link to={`/product-manager/projects/${project.id}`}>
              <CheckSquare className="h-4 w-4 mr-2" />
              View Tasks
            </Link>
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default LiveProjectCard;